import { Color, MeshStandardMaterial, Vector2, type IUniform } from 'three';
import { PALETTE, FINALE } from '@/config';

/**
 * BuildingMaterial extends MeshStandardMaterial so buildings keep real PBR
 * lighting and cast/receive shadows, while injecting Palimpsest's signature
 * behaviour through onBeforeCompile:
 *
 *   1. GROWTH  — per-instance vertical reveal. The mesh's vertices are scaled in
 *      the shader by `aHeight * aGrowth`, so a building literally rises out of the
 *      ground rather than popping in. No model swapping, ever (a core design rule).
 *
 *   2. GROWTH TIP — while a building is still rising, a warm emissive band rides
 *      its growing edge, reading as "the city is building this right now".
 *
 *   3. FACADE — floor striations (windows) and base ambient occlusion give the
 *      low-poly boxes just enough texture to feel architectural, all procedurally,
 *      so we ship zero facade textures.
 *
 * Shared uniforms (uGlow, uInk, uGlowAmount, uTime) are exposed so the environment
 * / mood system can drive the whole skyline's warmth and ink weight in real time.
 */
export interface BuildingUniforms {
  uTime: IUniform<number>;
  uGlow: IUniform<Color>;
  uInk: IUniform<Color>;
  uGlowAmount: IUniform<number>;
  uFloorHeight: IUniform<number>;
  /* Finale "memory wave" — see the vertex shader for the staggering algebra. */
  uMemory: IUniform<number>;
  uMemoryCentre: IUniform<Vector2>;
  uMemorySpan: IUniform<number>;
  uMemoryWave: IUniform<number>;
}

export class BuildingMaterial extends MeshStandardMaterial {
  readonly uniforms: BuildingUniforms;

  constructor() {
    super({
      color: new Color(PALETTE.building),
      roughness: 0.92,
      metalness: 0.0,
      flatShading: false,
    });

    this.uniforms = {
      uTime: { value: 0 },
      uGlow: { value: new Color(PALETTE.glow) },
      uInk: { value: new Color(PALETTE.accent) },
      uGlowAmount: { value: 0.0 },
      uFloorHeight: { value: 3.2 },
      // Memory wave defaults: dormant (uMemory 0). uMemoryWave carries FINALE.waveAmount
      // as its baked-in default (the field may overwrite); span ≈ half the city extent.
      uMemory: { value: 0.0 },
      uMemoryCentre: { value: new Vector2(0, 0) },
      uMemorySpan: { value: 300 },
      uMemoryWave: { value: FINALE.waveAmount },
    };

    this.onBeforeCompile = (shader) => {
      Object.assign(shader.uniforms, this.uniforms);

      shader.vertexShader = shader.vertexShader
        .replace(
          '#include <common>',
          /* glsl */ `#include <common>
            attribute float aHeight;
            attribute float aGrowth;
            attribute float aTone;
            attribute float aSeed;
            attribute float aStyle;
            attribute float aSeedH;
            uniform float uMemory;
            uniform vec2 uMemoryCentre;
            uniform float uMemorySpan;
            uniform float uMemoryWave;
            varying float vEchoWorldY;
            varying float vEchoTopY;
            varying float vEchoGrowth;
            varying float vEchoTone;
            varying float vEchoSeed;
            varying float vEchoStyle;
            varying vec3 vEchoWorldNormal;
            varying vec2 vEchoWorldXZ;
          `,
        )
        .replace(
          '#include <begin_vertex>',
          /* glsl */ `#include <begin_vertex>
            // --- Finale memory wave -------------------------------------------
            // Each instance's world-XZ origin is the translation column of its
            // (model * instance) matrix — independent of the box's own scale.
            #ifdef USE_INSTANCING
              vec3 echoOrigin = (modelMatrix * instanceMatrix * vec4(0.0, 0.0, 0.0, 1.0)).xyz;
            #else
              vec3 echoOrigin = (modelMatrix * vec4(0.0, 0.0, 0.0, 1.0)).xyz;
            #endif
            float echoDistNorm =
              clamp(distance(echoOrigin.xz, uMemoryCentre) / max(uMemorySpan, 1.0), 0.0, 1.0);
            // Local memory m in [0,1]: mix aHeight (built) -> aSeedH (seed) by m.
            // K = uMemoryWave. The -K*distNorm term makes near-centre instances carry
            // the LARGEST m, so as uMemory rises 0->1 they reach full memory (seed
            // height) first: the collapse sweeps OUTWARD from the wave centre. As
            // uMemory falls 1->0 the far ring is released first and the still-collapsed
            // core last, so the rebloom closes back IN toward the centre. (A single
            // monotone gradient necessarily gives collapse-outward / rebloom-inward.)
            float echoMem =
              clamp(uMemory * (1.0 + uMemoryWave) - uMemoryWave * echoDistNorm, 0.0, 1.0);
            float echoEffH = mix(aHeight, aSeedH, echoMem);

            // position.y is in [0,1] (unit box anchored at its base).
            transformed.y = position.y * echoEffH * aGrowth;
            vEchoWorldY = transformed.y;
            vEchoTopY = echoEffH * aGrowth;
            vEchoGrowth = aGrowth;
            vEchoTone = aTone;
            vEchoSeed = aSeed;
            vEchoStyle = aStyle;

            // World-space normal & footprint position, so the fragment shader can
            // draw facade marks aligned to each wall and apply a fake-GI tonal shift.
            vec3 echoN = normal;
            #ifdef USE_INSTANCING
              echoN = mat3(instanceMatrix) * echoN;
              vec4 echoWP = modelMatrix * instanceMatrix * vec4(transformed, 1.0);
            #else
              vec4 echoWP = modelMatrix * vec4(transformed, 1.0);
            #endif
            vEchoWorldNormal = normalize(mat3(modelMatrix) * echoN);
            vEchoWorldXZ = echoWP.xz;
          `,
        );

      shader.fragmentShader = shader.fragmentShader
        .replace(
          '#include <common>',
          /* glsl */ `#include <common>
            uniform float uTime;
            uniform vec3 uGlow;
            uniform vec3 uInk;
            uniform float uGlowAmount;
            uniform float uFloorHeight;
            varying float vEchoWorldY;
            varying float vEchoTopY;
            varying float vEchoGrowth;
            varying float vEchoTone;
            varying float vEchoSeed;
            varying float vEchoStyle;
            varying vec3 vEchoWorldNormal;
            varying vec2 vEchoWorldXZ;

            float echoHash(float n) { return fract(sin(n) * 43758.5453123); }
          `,
        )
        .replace(
          '#include <color_fragment>',
          /* glsl */ `#include <color_fragment>
            // Per-building brightness variation keeps the skyline from looking flat.
            diffuseColor.rgb *= mix(0.82, 1.10, vEchoTone);

            int echoStyle = int(vEchoStyle + 0.5);

            // A horizontal coordinate that runs ALONG each wall (perpendicular to
            // its normal), so vertical marks stay vertical on every face. And a
            // wall mask that fades marks out on roofs / floors.
            float echoHoriz =
              abs(vEchoWorldNormal.x) > abs(vEchoWorldNormal.z) ? vEchoWorldXZ.y : vEchoWorldXZ.x;
            float echoWall = 1.0 - clamp(abs(vEchoWorldNormal.y), 0.0, 1.0);
            float echoY = vEchoWorldY;
            float echoWarm = 0.0; // interior-glow window mask, gated by uGlowAmount.

            if (echoStyle == 0) {
              // tower — vertical mullion lines, plus faint floor reveals.
              float m = fract(echoHoriz / 1.4 + vEchoSeed * 0.11);
              float mull = (1.0 - smoothstep(0.0, 0.05, m)) + smoothstep(0.95, 1.0, m);
              diffuseColor.rgb = mix(diffuseColor.rgb, uInk * 0.7, mull * echoWall * 0.35);
              float fl = fract(echoY / 3.0);
              float fline = 1.0 - smoothstep(0.0, 0.045, fl);
              diffuseColor.rgb = mix(diffuseColor.rgb, uInk * 0.7, fline * echoWall * 0.14);
              echoWarm = fline * echoWall * step(0.55, echoHash(floor(echoY / 3.0) + vEchoSeed * 7.0));
            } else if (echoStyle == 1) {
              // slab — bold horizontal strata.
              float b = fract(echoY / 2.0 + vEchoSeed * 0.05);
              float band = (1.0 - smoothstep(0.0, 0.09, b)) + smoothstep(0.86, 1.0, b);
              diffuseColor.rgb = mix(diffuseColor.rgb, uInk * 0.6, band * echoWall * 0.4);
            } else if (echoStyle == 2) {
              // court — quiet paper with sparse punched windows.
              vec2 cell = vec2(floor(echoHoriz / 1.6), floor(echoY / 2.2));
              float win = step(0.82, echoHash(dot(cell, vec2(12.3, 7.1)) + vEchoSeed));
              float wx = fract(echoHoriz / 1.6);
              float wy = fract(echoY / 2.2);
              float wshape =
                smoothstep(0.35, 0.45, wx) * (1.0 - smoothstep(0.55, 0.65, wx)) *
                smoothstep(0.3, 0.4, wy) * (1.0 - smoothstep(0.6, 0.7, wy));
              float mask = win * wshape * echoWall;
              diffuseColor.rgb = mix(diffuseColor.rgb, uInk * 0.6, mask * 0.4);
              echoWarm = mask;
            } else if (echoStyle == 3) {
              // stack — irregular, densely hashed windows.
              vec2 cell = vec2(floor(echoHoriz / 1.3), floor(echoY / 1.8));
              float win = step(0.5, echoHash(dot(cell, vec2(19.7, 3.3)) + vEchoSeed * 2.0));
              float wx = fract(echoHoriz / 1.3);
              float wy = fract(echoY / 1.8);
              float wshape =
                smoothstep(0.25, 0.35, wx) * (1.0 - smoothstep(0.6, 0.7, wx)) *
                smoothstep(0.25, 0.35, wy) * (1.0 - smoothstep(0.65, 0.75, wy));
              float mask = win * wshape * echoWall;
              diffuseColor.rgb = mix(diffuseColor.rgb, uInk * 0.55, mask * 0.45);
              echoWarm = mask;
            } else if (echoStyle == 4) {
              // spire — a near-ink dark body (its glowing tip is added in emissive).
              diffuseColor.rgb = mix(diffuseColor.rgb, uInk, 0.6);
            }
            // echoStyle == 5 (terrace): plain plaster — no facade marks.

            // Warm interior light through windows, gated by the mood system.
            diffuseColor.rgb += uGlow * echoWarm * uGlowAmount * 0.7;

            // Ambient occlusion pooling at the very base grounds the building.
            diffuseColor.rgb *= mix(0.62, 1.0, smoothstep(0.0, 2.2, vEchoWorldY));

            // Roofline INK EDGE — darken the final ~0.25m of each wall below the top
            // so every roof reads as a crisp ink outline (roofs themselves stay paper).
            float roofEdge = smoothstep(vEchoTopY - 0.25, vEchoTopY, vEchoWorldY) * echoWall;
            diffuseColor.rgb = mix(diffuseColor.rgb, uInk, roofEdge * 0.35);

            // Fake GI — a gentle warm/cool tonal shift by world-facing direction
            // (sun side slightly warm, opposite slightly cool), kept within ±6%.
            float gi = dot(vEchoWorldNormal, normalize(vec3(0.55, 0.15, 0.42)));
            float giAmt = clamp(gi, -1.0, 1.0) * 0.06;
            diffuseColor.rgb *= vec3(1.0 + giAmt, 1.0 + giAmt * 0.2, 1.0 - giAmt);
          `,
        )
        .replace(
          '#include <emissivemap_fragment>',
          /* glsl */ `#include <emissivemap_fragment>
            // Warm construction glow riding the growing edge.
            float distToTip = abs(vEchoWorldY - vEchoTopY);
            float tip = (1.0 - smoothstep(0.0, 1.4, distToTip)) * (1.0 - vEchoGrowth);
            totalEmissiveRadiance += uGlow * tip * 1.6;

            int emStyle = int(vEchoStyle + 0.5);
            if (emStyle == 0) {
              // tower — a warm emissive crown band just below the roofline.
              float crown =
                smoothstep(vEchoTopY - 2.6, vEchoTopY - 0.3, vEchoWorldY) *
                (1.0 - smoothstep(vEchoTopY - 0.3, vEchoTopY, vEchoWorldY));
              totalEmissiveRadiance += uGlow * crown * uGlowAmount * 0.9 * vEchoGrowth;
            } else if (emStyle == 4) {
              // spire — a softly glowing tip, scaled by the mood glow.
              float tipGlow = smoothstep(vEchoTopY - 1.2, vEchoTopY, vEchoWorldY);
              totalEmissiveRadiance += uGlow * tipGlow * (0.25 + uGlowAmount * 0.9) * vEchoGrowth;
            }
          `,
        );
    };
  }

  tick(elapsed: number): void {
    this.uniforms.uTime.value = elapsed;
  }
}
